import { Fragment, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useGetAuthenticatedUser } from 'Flux/Selector/index.js'
import type { IOrganization } from 'Model/User/IUser.js'
import { tracingAxios } from 'Repository/Sentry.js'
import { Environment } from 'environment.js'
import { addMessage } from '../../../Flux/Slice/Application/ApplicationSlice.js'
import { errorMessage } from '../../../Model/Message/IMessage.js'
import { trackBilling } from '../../../Services/Telemetry/index.js'
import {
  calculatePrice,
  sliderToSamples,
  samplesToSlider,
  SLIDER_STEPS,
  PLAN_CONTENT,
  findPlan,
  formatPrice,
  formatPricePerSample,
  MAX_SAMPLES
} from './billingUtils.js'
import type { UserType } from './billingUtils.js'
import type { PlanDTO } from '../../../Generated/PlanDTO.ts'
import type { CreditItemDTO } from '../../../Generated/CreditItemDTO.ts'
import type { UpgradeRequest } from '../../../Generated/UpgradeRequest.ts'
import type { CheckoutResponse } from '../../../Generated/CheckoutResponse.ts'
import type { PortalResponse } from '../../../Generated/PortalResponse.ts'

interface PlanSelectionProps {
  plans: PlanDTO[]
  creditItems: CreditItemDTO[]
  org: IOrganization
  /** True if the org currently has an active or non-renewing Chargebee subscription. */
  subscribed: boolean
}

const STATUS_STYLES: Record<string, { label: string; className: string }> = {
  active: { label: 'Active', className: 'badge-success' },
  non_renewing: { label: 'Non-Renewing', className: 'badge-warning' },
  cancelled: { label: 'Cancelled', className: 'badge-error' },
  paused: { label: 'Paused', className: 'badge-warning' },
  deleted: { label: 'Deleted', className: 'badge-error' }
}

const COMPARE_DATA: Record<UserType, { area: string; items: { name: string; base: boolean; insight: boolean }[] }[]> = {
  lab: [
    {
      area: 'Marketplace',
      items: [
        { name: 'Public lab profile', base: false, insight: true },
        { name: 'Receive orders from customers', base: false, insight: true }
      ]
    },
    {
      area: 'Creating Orders',
      items: [
        { name: 'Create orders', base: true, insight: true },
        { name: 'File management', base: true, insight: true },
        { name: 'Reruns', base: true, insight: true }
      ]
    },
    {
      area: 'Processing Orders',
      items: [
        { name: 'Pipeline processing', base: true, insight: true },
        { name: 'Basic reports', base: true, insight: true },
        { name: 'Upgraded reports', base: false, insight: true }
      ]
    },
    {
      area: 'Projects & Analysis',
      items: [
        { name: 'Organize orders in projects', base: false, insight: true },
        { name: 'Interactive project views', base: false, insight: true },
        { name: 'Advanced taxonomy filters', base: false, insight: true },
        { name: 'Cross-order analysis', base: false, insight: true }
      ]
    },
    {
      area: 'Data & Security',
      items: [
        { name: 'Secure data hosting', base: true, insight: true },
        { name: 'Data export', base: true, insight: true },
        { name: 'Project sharing', base: false, insight: true }
      ]
    }
  ],
  customer: [
    {
      area: 'Marketplace',
      items: [
        { name: 'Browse labs', base: true, insight: true },
        { name: 'Place orders with labs', base: true, insight: true }
      ]
    },
    {
      area: 'Order Management',
      items: [
        { name: 'Track orders', base: true, insight: true },
        { name: 'File management', base: true, insight: true },
        { name: 'Basic reports', base: true, insight: true }
      ]
    },
    {
      area: 'Projects & Analysis',
      items: [
        { name: 'Organize datasets in projects', base: false, insight: true },
        { name: 'Interactive visualizations', base: false, insight: true },
        { name: 'Advanced taxonomy filters', base: false, insight: true },
        { name: 'Groupings & comparisons', base: false, insight: true }
      ]
    },
    {
      area: 'Collaboration',
      items: [
        { name: 'Ready-to-share reports', base: true, insight: true },
        { name: 'Project sharing', base: false, insight: true }
      ]
    },
    {
      area: 'Data & Security',
      items: [
        { name: 'Full data ownership', base: true, insight: true },
        { name: 'Secure data hosting', base: true, insight: true },
        { name: 'Data export', base: true, insight: true }
      ]
    }
  ]
}

export const PlanSelection = ({ plans, creditItems, org, subscribed }: PlanSelectionProps) => {
  const dispatch = useDispatch()
  const user = useGetAuthenticatedUser()
  const isAdmin = user?.roles?.includes('org-admin') ?? false
  const compareModalRef = useRef<HTMLDialogElement>(null)

  const currentPlan = useMemo(() => {
    if (!subscribed) return null
    return plans.find((p) => p.id === org.chargebeePlanId) ?? null
  }, [subscribed, plans, org.chargebeePlanId])

  const isCustomPlan = subscribed && !!org.chargebeePlanId && !currentPlan

  const currentUserType: UserType = (currentPlan?.orgType ?? org.type) === 'CUSTOMER' ? 'customer' : 'lab'
  const currentBillingPeriod: 'month' | 'year' = currentPlan?.billingPeriodUnit === 'year' ? 'year' : 'month'
  const currentIsAcademic = (currentPlan?.commercialType ?? org.commercialType) === 'ACADEMIC'

  const [userType, setUserType] = useState<UserType>(currentUserType)
  const [isAcademic, setIsAcademic] = useState<boolean>(currentIsAcademic)
  const [billingPeriod, setBillingPeriod] = useState<'month' | 'year'>(currentBillingPeriod)
  const [sampleQuantity, setSampleQuantity] = useState(0)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [portalLoading, setPortalLoading] = useState(false)
  /* for now its not easily decidable if the user has subscribed to a non base plan */
  const [isCollapsed, setIsCollapsed] = useState(isCustomPlan)

  const orgTypeFilter = userType === 'lab' ? 'LABORATORY' : 'CUSTOMER'
  const commercialTypeFilter = isAcademic ? 'ACADEMIC' : 'COMMERCIAL'

  const basePlan = findPlan(plans, orgTypeFilter, commercialTypeFilter, billingPeriod, 'base')
  const insightPlan = findPlan(plans, orgTypeFilter, commercialTypeFilter, billingPeriod, 'insight')

  const creditItem = creditItems[0]
  const sampleCostCents = creditItem && sampleQuantity > 0 ? calculatePrice(creditItem, sampleQuantity) : 0

  const statusInfo = STATUS_STYLES[org.chargebeeSubscriptionStatus ?? ''] ?? { label: 'Unknown', className: 'badge-ghost' }

  const handleCheckoutOrUpgrade = async (plan: PlanDTO) => {
    setActionLoading(plan.id)
    trackBilling('checkout_initiated', { plan_id: plan.id, is_upgrade: subscribed })
    try {
      const endpoint = subscribed ? '/v1/chargebee/upgrade' : '/v1/chargebee/checkout'
      const payload: UpgradeRequest = {
        planItemPriceId: plan.id,
        redirectUrl: `${window.location.origin}/billing?checkout=success`
      }
      const { data } = await tracingAxios.post<CheckoutResponse>(`${Environment.API_URL}${endpoint}`, payload, {
        withCredentials: true
      })
      window.location.href = data.url
    } catch (e) {
      console.error('Plan change failed', e)
      dispatch(addMessage(errorMessage('Plan change failed. Please try again or contact support.')))
      setActionLoading(null)
    }
  }

  const openPortal = async () => {
    setPortalLoading(true)
    trackBilling('manage_subscription')
    const popup = window.open('about:blank', '_blank', 'width=900,height=700,scrollbars=yes')
    try {
      const { data } = await tracingAxios.get<PortalResponse>(`${Environment.API_URL}/v1/chargebee/portal`, {
        withCredentials: true
      })
      if (popup && !popup.closed) {
        popup.location.href = data.url
        const pollClose = setInterval(async () => {
          if (!popup.closed) return
          clearInterval(pollClose)
          try {
            await tracingAxios.get(`${Environment.API_URL}/v1/chargebee/refresh-session`, { withCredentials: true })
          } catch {
            /* interceptor handles side-effect */
          }
        }, 500)
      } else {
        window.location.href = data.url
      }
    } catch (e) {
      popup?.close()
      console.error('Failed to open portal', e)
      dispatch(addMessage(errorMessage('Failed to open subscription portal. Please try again or contact support.')))
    } finally {
      setPortalLoading(false)
    }
  }

  const handleSampleInput = (value: string) => {
    const num = Math.min(MAX_SAMPLES, Math.max(0, parseInt(value) || 0))
    setSampleQuantity(num)
  }

  const isCustomerBase = (tier: 'base' | 'insight') => orgTypeFilter === 'CUSTOMER' && tier === 'base'

  const academicBg = isAcademic ? (userType === 'lab' ? 'bg-amber-50/60' : 'bg-purple-50/60') : ''
  const statusLabel = `${isAcademic ? 'Academic' : 'Commercial'} ${userType === 'lab' ? 'Lab' : 'User'}`

  return (
    <div data-testid="plan-selection" className="space-y-8">
      <div className="text-center">
        <h2 className="text-2xl font-bold">
          {subscribed ? (
            <>
              Your Plan:{' '}
              <span data-testid="current-plan" className="text-primary">
                {currentPlan?.name ?? 'Custom Plan'}
              </span>
            </>
          ) : (
            'Choose Your Plan'
          )}
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          {subscribed
            ? 'Compare plans, switch tier, or estimate sample costs at different volumes.'
            : 'Select your organization type and configure your subscription'}
        </p>
        {subscribed && (
          <div className="flex flex-col items-center gap-2 mt-2">
            {org.chargebeeEntitlements && org.chargebeeEntitlements.length > 0 && (
              <div className="flex flex-wrap justify-center gap-1 max-w-xl">
                {org.chargebeeEntitlements.map((e) => (
                  <span key={e} className="badge badge-ghost badge-xs uppercase opacity-70">
                    {e.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            )}
            <button className="btn btn-ghost btn-xs" onClick={() => setIsCollapsed(!isCollapsed)}>
              {isCollapsed ? 'Show Plan Picker' : 'Hide Plan Picker'}
            </button>
          </div>
        )}
      </div>

      {!isCollapsed && (
        <div className="space-y-8">
          {isCustomPlan && (
            <div className="alert alert-warning max-w-4xl mx-auto shadow-sm">
              <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div>
                <h3 className="font-bold text-sm">Custom Plan Detected</h3>
                <div className="text-xs">
                  Your organization is currently on a custom subscription plan. Switching to a standard plan below might result in the loss
                  of custom conditions and you may not be able to switch back without assistance.
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-center">
            <div className="bg-base-200 p-1 rounded-full inline-flex relative">
              <div
                className="absolute top-1 bottom-1 rounded-full bg-white shadow transition-all duration-200 ease-out"
                style={{
                  width: 'calc(50% - 4px)',
                  left: userType === 'lab' ? '4px' : 'calc(50% + 0px)'
                }}
              />
              <button
                className={`relative z-10 px-6 py-2 text-sm font-medium rounded-full transition-colors duration-200 ${
                  userType === 'lab' ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setUserType('lab')}
              >
                I am a Lab
              </button>
              <button
                className={`relative z-10 px-6 py-2 text-sm font-medium rounded-full transition-colors duration-200 ${
                  userType === 'customer' ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setUserType('customer')}
              >
                I am a Customer
              </button>
            </div>
          </div>

          <div
            className={`bg-white border border-gray-200 rounded-xl p-5 shadow-sm max-w-4xl mx-auto transition-colors duration-300 ${academicBg}`}
          >
            <div className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col items-center gap-2">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-medium ${!isAcademic ? 'text-gray-900' : 'text-gray-400'}`}>Commercial</span>
                    <input
                      type="checkbox"
                      className="toggle toggle-primary toggle-sm"
                      checked={isAcademic}
                      onChange={(e) => setIsAcademic(e.target.checked)}
                      disabled={!isAdmin}
                    />
                    <span className={`text-xs font-medium ${isAcademic ? 'text-gray-900' : 'text-gray-400'}`}>Academic</span>
                  </div>
                  <span className="text-xs text-gray-500">{statusLabel}</span>
                </div>

                <div className="flex flex-col items-center gap-2">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-medium ${billingPeriod === 'month' ? 'text-gray-900' : 'text-gray-400'}`}>Monthly</span>
                    <input
                      type="checkbox"
                      className="toggle toggle-primary toggle-sm"
                      checked={billingPeriod === 'year'}
                      onChange={(e) => setBillingPeriod(e.target.checked ? 'year' : 'month')}
                      disabled={!isAdmin}
                    />
                    <span className={`text-xs font-medium ${billingPeriod === 'year' ? 'text-gray-900' : 'text-gray-400'}`}>Annual</span>
                  </div>
                  <span className="text-xs text-gray-500">
                    {billingPeriod === 'month' ? 'Monthly Billing' : 'Annual Billing (-10% on License)'}
                  </span>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-700">Estimated Samples / Year</span>
                  <input
                    type="number"
                    min={0}
                    value={sampleQuantity}
                    onChange={(e) => handleSampleInput(e.target.value)}
                    className="input input-bordered input-sm w-24 text-right font-mono text-sm"
                  />
                </div>
                <input
                  type="range"
                  min={0}
                  max={SLIDER_STEPS}
                  value={samplesToSlider(sampleQuantity)}
                  onChange={(e) => setSampleQuantity(sliderToSamples(Number(e.target.value)))}
                  className="range range-primary range-sm w-full"
                />
                <div className="relative h-4 text-[10px] text-gray-400">
                  {[
                    { value: 0, anchor: 'start' as const },
                    { value: 100, anchor: 'mid' as const },
                    { value: 1000, anchor: 'mid' as const },
                    { value: MAX_SAMPLES, anchor: 'end' as const }
                  ].map(({ value, anchor }) => {
                    const pct = (samplesToSlider(value) / SLIDER_STEPS) * 100
                    const transform = anchor === 'start' ? undefined : anchor === 'end' ? 'translateX(-100%)' : 'translateX(-50%)'
                    return (
                      <span key={value} className="absolute top-0" style={{ left: `${pct}%`, transform }}>
                        {value.toLocaleString()}
                      </span>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="w-full max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row gap-6 md:items-stretch">
              {(['base', 'insight'] as const).map((tier) => {
                const plan = tier === 'base' ? basePlan : insightPlan
                const isInsight = tier === 'insight'
                const content = PLAN_CONTENT[userType][tier]
                const isFree = isCustomerBase(tier)
                const hasPlan = plan !== undefined
                const isCurrent = subscribed && plan !== undefined && plan.id === org.chargebeePlanId
                const showRecommended = isInsight && !subscribed

                const planPeriod = plan?.billingPeriodUnit ?? billingPeriod
                const periodLabel = planPeriod === 'month' ? 'p.m. (billed monthly)' : 'per year (billed annually)'
                const licenseCents = plan?.priceInCents ?? 0
                const currency = plan?.currencyCode ?? 'EUR'
                const totalCents = licenseCents

                const cardBorder = isCurrent
                  ? 'border-primary shadow-md'
                  : isInsight && !subscribed
                    ? 'border-primary shadow-md'
                    : 'border-gray-200'

                const reserveBannerSpace = !subscribed

                return (
                  <div key={tier} className="md:flex-1 md:basis-0 md:min-w-0 flex">
                    <div
                      className={`relative w-full bg-white rounded-xl border-2 flex flex-col overflow-hidden transition-colors duration-300 min-h-[32rem] ${cardBorder} ${
                        isAcademic ? academicBg : ''
                      }`}
                    >
                      {showRecommended && (
                        <div className="absolute -top-0 left-0 right-0">
                          <div className="bg-primary text-white text-xs font-semibold px-3 py-1 text-center">Recommended</div>
                        </div>
                      )}

                      <div className={`p-6 flex-1 flex flex-col min-w-0 ${reserveBannerSpace ? 'pt-10' : ''}`}>
                        <div className="flex items-start justify-between gap-2 min-h-14">
                          <div className="min-w-0 flex-1">
                            <h3 className="text-xl font-bold truncate">{content.name}</h3>
                            <p className="text-sm text-gray-500 mt-0.5 min-h-10">{content.desc}</p>
                          </div>
                          {isCurrent && (
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="badge badge-primary badge-sm">Current Plan</span>
                              <span data-testid="subscription-status" className={`badge badge-sm ${statusInfo.className}`}>
                                {statusInfo.label}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="mt-4 min-h-9">
                          {isFree && sampleQuantity === 0 ? (
                            <>
                              <span className="text-3xl font-bold">Free access</span>
                              <p className="text-sm text-gray-500 mt-1">No subscription fee</p>
                            </>
                          ) : hasPlan ? (
                            <>
                              <span className="text-3xl font-bold">{formatPrice(licenseCents, currency)}</span>
                              <span className="text-gray-500 text-sm ml-1">{periodLabel}</span>
                            </>
                          ) : (
                            <>
                              <span className="text-3xl font-bold">Contact us</span>
                              <p className="text-sm text-gray-500 mt-1">Custom pricing available</p>
                            </>
                          )}
                        </div>

                        <ul className="mt-6 space-y-2.5 flex-1">
                          {content.features.map((f, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm">
                              <svg className="w-4 h-4 text-green-500 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              <span>
                                {f.text}
                                {f.tip && (
                                  <span className="tooltip tooltip-right ml-1" data-tip={f.tip}>
                                    <svg className="w-3.5 h-3.5 inline text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                      />
                                    </svg>
                                  </span>
                                )}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="mt-auto border-t border-gray-100 bg-gray-50/80 px-6 py-4">
                        <div className="space-y-1.5 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-500">License Fee</span>
                            <span>{isFree ? 'Free' : hasPlan ? formatPrice(licenseCents, currency) : '—'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">
                              Sample Costs
                              {sampleQuantity > 0 && creditItem && (
                                <span className="text-gray-400 text-xs ml-1">
                                  ({sampleQuantity.toLocaleString()} x{' '}
                                  {formatPricePerSample(
                                    calculatePrice(creditItem, sampleQuantity) / sampleQuantity,
                                    creditItem.currencyCode
                                  )}
                                  )
                                </span>
                              )}
                            </span>
                            <span>{sampleQuantity > 0 ? `~${formatPrice(sampleCostCents, currency)} (one-time)` : '—'}</span>
                          </div>
                          <div className="border-t border-gray-200 pt-1.5 flex justify-between font-semibold">
                            <span>Est. Subscription / {planPeriod === 'month' ? 'mo' : 'yr'}</span>
                            <span>{hasPlan ? formatPrice(totalCents, currency) : '—'}</span>
                          </div>
                        </div>

                        {isCurrent ? (
                          <button className="btn btn-outline mt-4 w-full" onClick={openPortal} disabled={portalLoading}>
                            {portalLoading ? <span className="loading loading-spinner loading-sm" /> : 'Manage Subscription'}
                          </button>
                        ) : hasPlan ? (
                          <button
                            className={`btn mt-4 w-full ${isInsight ? 'btn-primary' : 'btn-outline'}`}
                            onClick={() => handleCheckoutOrUpgrade(plan)}
                            disabled={actionLoading !== null}
                          >
                            {actionLoading === plan.id ? (
                              <span className="loading loading-spinner loading-sm" />
                            ) : subscribed ? (
                              'Switch to this plan'
                            ) : (
                              'Subscribe'
                            )}
                          </button>
                        ) : (
                          <button className={`btn mt-4 w-full btn-disabled ${isInsight ? 'btn-primary' : 'btn-outline'}`} disabled>
                            Contact us
                          </button>
                        )}

                        <button
                          className="btn btn-ghost btn-xs w-full mt-2 text-gray-400"
                          onClick={() => compareModalRef.current?.showModal()}
                        >
                          Compare Features
                        </button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      <dialog ref={compareModalRef} className="modal">
        <div className="modal-box max-w-2xl">
          <form method="dialog">
            <button className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">✕</button>
          </form>
          <h3 className="font-bold text-lg mb-4">Compare Plans — {userType === 'lab' ? 'Laboratory' : 'Customer'}</h3>
          <div className="overflow-x-auto">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th className="w-1/2">Feature</th>
                  <th className="text-center">BASE</th>
                  <th className="text-center">INSIGHT</th>
                </tr>
              </thead>
              <tbody>
                {COMPARE_DATA[userType].map((section) => (
                  <Fragment key={section.area}>
                    <tr>
                      <td colSpan={3} className="font-semibold bg-base-200 text-xs uppercase tracking-wide text-gray-500 pt-3">
                        {section.area}
                      </td>
                    </tr>
                    {section.items.map((item) => (
                      <tr key={item.name}>
                        <td className="text-sm">{item.name}</td>
                        <td className="text-center">
                          {item.base ? (
                            <svg className="w-4 h-4 text-green-500 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="text-center">
                          {item.insight ? (
                            <svg className="w-4 h-4 text-green-500 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button>close</button>
        </form>
      </dialog>
    </div>
  )
}
